import { apiRequest } from '@/domains/api';
import type {
  IInternalFieldHistory,
  IInternalFormSchema,
  IInternalValue,
  INoteInitials,
  INoteInitialsBody,
  InternalPayload
} from './internal-field-contracts';

/**
 * survey-api's internal fields — the staff-only values a record page edits beside what a
 * respondent answered (survey-api's SR-222, `InternalFieldValueEndpoints.cs`): one survey's staff
 * form (`/admin/surveys/{code}/internal-fields`), the value write and clear addressed by a
 * response (`/admin/responses/{id}/internal-fields/{code}`), and one field's history about a
 * participant (`/admin/participants/{id}/internal-fields/{code}/events`). The sole reader of
 * those three prefixes' internal-field routes. What the hub carries of them — each member's
 * record, as `internal` — arrives through `participants-api.ts` with the rest of the hub, the
 * participant surface's one reader.
 *
 * **A schema is read once per survey per page load.** It changes only when the catalogue does,
 * and every part of the hub drawing one survey asks for it, so a module-level promise per survey
 * code answers them all — dropped when it fails, so the next screen asks again, and taking no
 * `AbortSignal`, because the first screen to leave would cancel it for every other: the SEC ADV
 * catalogue's rule (`sec-adv-api.ts`) and its reasons. A 2xx that carries no `groups` is a
 * failed read, refused inside the shared promise so it is forgotten like any other failure —
 * and so is a group carrying no `fields` list, which would otherwise throw while the hub draws
 * (this app has no error boundary). A screen that showed a failed read learns of a later one
 * that lands (`onSchema`): the header's chips draw from the schema too, and a part's "Read again"
 * is what reads it afresh. Rejected: re-reading the schema with the hub's poll — a document that
 * moves only when an administrator authors, read every five seconds per survey.
 *
 * **A write's 2xx that carries no value is a failed write's answer**, refused like the schema's:
 * the hub holds the answer over the field until its re-read lands, and a `null` held there would
 * draw a value just saved as emptied.
 *
 * **Every path segment is encoded, and a dot segment never sent.** A field code is one segment by
 * survey-api's own rule (`^[a-z0-9]+([._][a-z0-9]+)*$`), but the codes here come from documents,
 * and the URL parser behind `fetch` removes `.` and `..` — `%2e` included — so such a value would
 * reach a different route carrying the caller's bearer (`participants-api.ts`' `getHub`). It is
 * refused before the request instead.
 *
 * **A write quotes the cell's version, never an `ETag` read back** (SR-230, the lists design's
 * §7): the value carries its own `version` (0 for an empty cell), and the write and the clear send
 * it as `If-Match: "<version>"` — a stale one is 409 `cell-conflict` carrying the cell now
 * (`current`), nothing written. A caller with no version to quote (a value from an API before
 * survey-api's !97) passes none, and the write lands as it always did: survey-api makes the
 * header optional on this route, for exactly that. The write's 200 is the value as stored, its
 * new `version` in it — the `ETag` it also carries says the same and is not read.
 */

const BASE = '/api/v1/admin';

/** Values that could only reach a different route — see the module's comment. */
const NOT_A_SEGMENT = new Set(['', '.', '..']);

/** One path segment, encoded — or a refusal, before any request, for a dot segment. */
function segment(value: string): string {
  if (NOT_A_SEGMENT.has(value)) {
    throw new Error(`'${value}' cannot be sent as a path segment`);
  }
  return encodeURIComponent(value);
}

/** Each survey's schema read this page load, by survey code — see the module's comment. */
const schemaReads = new Map<string, Promise<IInternalFormSchema>>();

/** Who hears of a schema read that landed — see the module's comment. */
const schemaListeners = new Set<(surveyCode: string, schema: IInternalFormSchema) => void>();

function isObject(answer: unknown): answer is Record<string, unknown> {
  return typeof answer === 'object' && answer !== null;
}

/** The schema's groups are there, each with its fields — the least a part is drawn by. What is
 *  inside them is survey-api's, and not judged here. */
function isSchema(answer: unknown): answer is IInternalFormSchema {
  return (
    isObject(answer) &&
    Array.isArray(answer.groups) &&
    answer.groups.every((group) => isObject(group) && Array.isArray(group.fields))
  );
}

/** A stored value: its `{t, v}` document is there. */
function isValue(answer: unknown): answer is IInternalValue {
  return isObject(answer) && isObject(answer.value) && typeof answer.value.t === 'string';
}

function fieldPath(responseId: string, code: string): string {
  return `${BASE}/responses/${segment(responseId)}/internal-fields/${segment(code)}`;
}

/** The precondition a write sends — none without a version to quote (see the module's comment). */
function versionHeaders(version: number | undefined): Record<string, string> | undefined {
  return version === undefined ? undefined : { 'If-Match': `"${version}"` };
}

/** The note initials' one read per page load (`noteInitials`). */
let initialsRead: Nullable<Promise<INoteInitials[]>> = null;

export const internalFieldsApi = {
  /**
   * One survey's staff form, in render order, with nothing on it the caller may not see —
   * retired fields and options omitted, an `admin` field omitted for staff
   * (`internal_fields.read`). Shared per survey code for the page load; a 404 (no such survey)
   * throws like any failure, since the hub only asks for a survey one of its responses names.
   */
  schema(surveyCode: string): Promise<IInternalFormSchema> {
    const held = schemaReads.get(surveyCode);
    if (held !== undefined) return held;
    const started = Promise.resolve()
      .then(() => apiRequest<unknown>(`${BASE}/surveys/${segment(surveyCode)}/internal-fields`))
      .then((answer) => {
        if (!isSchema(answer)) {
          throw new Error('The answer to the staff form held no groups');
        }
        return answer;
      });
    schemaReads.set(surveyCode, started);
    started.then(
      (schema) => schemaListeners.forEach((listener) => listener(surveyCode, schema)),
      () => {
        if (schemaReads.get(surveyCode) === started) schemaReads.delete(surveyCode);
      }
    );
    return started;
  },

  /** Hears every schema read that lands from now on, whoever began it; answers the unsubscribe.
   *  For a screen drawing from a schema whose read it saw fail, which another screen may read
   *  again. */
  onSchema(listener: (surveyCode: string, schema: IInternalFormSchema) => void): () => void {
    schemaListeners.add(listener);
    return () => {
      schemaListeners.delete(listener);
    };
  },

  /**
   * Writes a value (`internal_fields.write`). The field's scope decides where it lands —
   * `participant_survey` on the response's participant and survey, `participant_cycle` on its
   * ranking cycle, `response` on the response — and the answer is the value as stored (a
   * `rich_text` value sanitised). Refusals: 404 bodiless, 403 for an `admin` field, 409
   * `field-managed`, 409 `code-in-use`, 409 `cell-conflict` (a stale `version`), 400
   * `invalid-body` with `problems[]` at `value`.
   */
  set(
    responseId: string,
    code: string,
    payload: InternalPayload,
    version?: number
  ): Promise<IInternalValue> {
    return Promise.resolve()
      .then(() =>
        apiRequest<unknown>(fieldPath(responseId, code), {
          method: 'PUT',
          body: { value: payload },
          headers: versionHeaders(version)
        })
      )
      .then((answer) => {
        if (!isValue(answer)) throw new Error('The answer to the write held no value');
        return answer;
      });
  },

  /**
   * A signed entry at the head of a notes log (SR-494): survey-api prefixes `text` with today's
   * date and the caller's initials (`M/D/YY XX - text`) and puts it before the log's first entry,
   * under the cell's lock — no version to quote, since nothing typed here can be stale. Refused as
   * `set` is; a blank text, or a field that is no text, 400 at `text`.
   */
  appendEntry(responseId: string, code: string, text: string): Promise<IInternalValue> {
    return Promise.resolve()
      .then(() =>
        apiRequest<unknown>(`${fieldPath(responseId, code)}/entries`, { method: 'POST', body: { text } })
      )
      .then((answer) => {
        if (!isValue(answer)) throw new Error('survey-api answered the entry with no value');
        return answer;
      });
  },

  /**
   * Who the initials in a notes log stand for (SR-494), dated — a tag changed hands over the years.
   * Read once per page load and shared, as a schema is; dropped when it fails, and read again
   * where `fresh` asks — the register's own screen.
   */
  noteInitials(fresh = false): Promise<INoteInitials[]> {
    if (fresh) initialsRead = null;
    initialsRead ??= apiRequest<{ items?: INoteInitials[] }>(`${BASE}/note-initials`)
      .then((answer) => {
        if (!Array.isArray(answer?.items)) throw new Error('survey-api answered note initials with no items');
        return answer.items;
      })
      .catch((error: unknown) => {
        initialsRead = null;
        throw error;
      });
    return initialsRead;
  },

  /** A register row added (SR-494's Setup, `internal_fields.author`) — 201 with the row; a bad
   *  member 400 at its name. Every screen's next read of the register sees it. */
  async createNoteInitials(body: INoteInitialsBody): Promise<INoteInitials> {
    const row = await apiRequest<INoteInitials>(`${BASE}/note-initials`, { method: 'POST', body });
    initialsRead = null;
    return row;
  },

  /** A register row replaced whole — a period closed by its `validTo`; 404 for an unknown row. */
  async updateNoteInitials(id: string, body: INoteInitialsBody): Promise<INoteInitials> {
    const row = await apiRequest<INoteInitials>(`${BASE}/note-initials/${segment(id)}`, {
      method: 'PUT',
      body
    });
    initialsRead = null;
    return row;
  },

  /** Clears a value — 204 whether or not one was stored; refused only `field-managed`,
   *  `cell-conflict`, 403 and 404 (a field retired or unbound is cleared, that being its repair). */
  clear(responseId: string, code: string, version?: number): Promise<void> {
    return Promise.resolve().then(async () => {
      await apiRequest<null>(fieldPath(responseId, code), {
        method: 'DELETE',
        headers: versionHeaders(version)
      });
    });
  },

  /**
   * One field's history about a participant: what is stored now and a page of its value events,
   * newest first, across every survey, cycle and response (`internal_fields.read`). Throws for
   * a field or participant the caller cannot see (404) and for a bad page (400
   * `invalid-query`). The page size is the caller's: survey-api's default (50) is a list's, not
   * a popover's.
   */
  history(
    participantId: string,
    code: string,
    page: number,
    pageSize: number,
    signal?: AbortSignal
  ): Promise<IInternalFieldHistory> {
    return Promise.resolve().then(() =>
      apiRequest<IInternalFieldHistory>(
        `${BASE}/participants/${segment(participantId)}/internal-fields/${segment(code)}` +
          `/events?page=${page}&pageSize=${pageSize}`,
        { signal }
      )
    );
  }
};
